import _snakeCase from 'lodash/snakeCase';
import React, { useEffect } from 'react';
import { useNotify } from '../../../../../../hooks';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { useModalStyles } from './styles';
import _get from 'lodash/get';

import { useTheme } from '@lifeomic/chroma-react/styles';
import { defineMessages, useIntl } from 'react-intl';
import useFhirSearch, {
  Hits,
} from '../../../../../Surveys/util/FhirSearch/useFhirSearch';
import MarkdownViewer from '../../../../../MarkdownViewer';

const messages = defineMessages({
  communicationViewError: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.CommunicationViewModal.Error',
    defaultMessage: 'Error loading communication',
  },
});

type CommunicationViewModalProps = {
  reference?: fhir.Reference;
  onDismiss: () => void;
};

export const useCommunication = ({
  reference,
}: {
  reference?: fhir.Reference;
}) => {
  const [resourceType, resourceId] = reference?.reference?.split('/') ?? [];
  const table = _snakeCase(resourceType);
  const [response, isLoading] = useFhirSearch<Hits<fhir.Resource>>({
    type: 'select',
    columns: '*',
    from: [{ table }],
    limit: [
      { type: 'number', value: 0 },
      { type: 'number', value: 1 },
    ],
    where: {
      type: 'elasticsearch',
      query: {
        bool: {
          must: [
            {
              term: {
                'id.keyword': resourceId,
              },
            },
          ],
        },
      },
    },
  });

  const resource = response?.hits?.hits[0]?._source ?? reference;

  const title =
    resource.meta?.tag?.find(
      tag => tag.system === 'http://lifeomic.com/fhir/source-type',
    )?.code ?? table;
  const contentString = _get(resource, 'payload[0].contentString');

  return { isLoading, title, resource, contentString };
};

export const CommunicationContent = (props: { reference?: fhir.Reference }) => {
  const { isLoading, resource, contentString } = useCommunication(props);
  const theme = useTheme();

  return isLoading ? (
    <Box justify="center" align="center">
      <DotLoader />
    </Box>
  ) : (
    <Box padding={4} style={{ fontFamily: theme.typography.fontFamily }}>
      {resource && <MarkdownViewer markdown={contentString} />}
    </Box>
  );
};

export const CommunicationViewModal = (props: CommunicationViewModalProps) => {
  const { onDismiss } = props;

  const classes = useModalStyles();

  const { formatMessage } = useIntl();
  const [notify] = useNotify();

  const { title, resource, isLoading } = useCommunication(props);

  const hasError = !isLoading && !resource;

  useEffect(() => {
    if (hasError) {
      notify(formatMessage(messages.communicationViewError), 'error');
      onDismiss();
    }
  }, [hasError, notify, formatMessage, onDismiss]);

  return (
    <Modal
      isOpen={true}
      size={1}
      onDismiss={onDismiss}
      contentClassName={classes.modalContent}
      title={title}
      data-testid="communication-view-modal"
    >
      <CommunicationContent {...props} />
    </Modal>
  );
};
